import React from 'react';
import { useSearchParams } from 'react-router-dom';

import CloudpassService from '../services/cloudpassService';
import AffiliatesService from '../services/affiliatesService';

interface AuthContextType {
  affServiceInstance: AffiliatesService | undefined;
  cpassServiceInstance: CloudpassService | undefined;
  configState: IConfigParams | undefined;
  deviceLoggedOut: () => void;
  init: (cp: string, idp: string, oneTimeToken: any, countryCode?: any) => void;
}
interface IConfigParams {
  authenticated: boolean;
  countryCode: string | null;
  cp: string | null;
  deviceToken: string | null;
  idp: string | null;
  isLoading: boolean;
}

const AuthContext = React.createContext<AuthContextType>(
  {} as AuthContextType
);

/**
 * Get Toolbox auth context
 * @returns auth Context
 */
export function useAuthGuard(): AuthContextType {
  const ctx = React.useContext(AuthContext);

  if (!ctx) {
    throw new Error('You need wrap your app with AuthGuardProvider HOC');
  }

  return ctx;
}

export function AuthGuardProvider({ children }: { children: React.ReactNode }) {
  const [configState, setConfigState] = React.useState<IConfigParams>({
    authenticated: false,
    countryCode: null,
    cp: null,
    deviceToken: null,
    idp: null,
    isLoading: true
  });
  const CLOUDPASS_API_REF = React.useRef<CloudpassService>();
  const AFFILIATES_API_REF = React.useRef<AffiliatesService>();

  AFFILIATES_API_REF.current = new AffiliatesService();

  const init = React.useCallback(async (cp: string, idp: string, toolboxUserToken?: string, countryCode?: string) => {
    //1. Initialize Cloudpass service for CP provided:
    CLOUDPASS_API_REF.current = new CloudpassService(cp);

    setConfigState({
      ...configState,
      authenticated: !!toolboxUserToken || false,
      countryCode: countryCode || configState.countryCode || null,
      cp,
      deviceToken: toolboxUserToken || null,
      idp,
      isLoading: false
    });
  },
  [configState]);

  const deviceLoggedOut = React.useCallback(() => {
    setConfigState((prevState) => ({
      ...prevState,
      authenticated: false,
      deviceToken: null
    }));
  }, []);

  const context = React.useMemo(
    () => ({
      affServiceInstance: AFFILIATES_API_REF.current,
      cpassServiceInstance: CLOUDPASS_API_REF.current,
      configState,
      deviceLoggedOut,
      init
    }),
    [
      AFFILIATES_API_REF,
      CLOUDPASS_API_REF,
      configState,
      deviceLoggedOut,
      init
    ]
  );


  return (
    <AuthContext.Provider value={context}>
    {children}
    </AuthContext.Provider>
  );
}

export function RequireAuth({ children }: { children: JSX.Element }) {
  const authGuard = useAuthGuard();
  const [searchParams] = useSearchParams();

  if (searchParams.size < 2) {
    throw Error('Missing required parameters!');
  }

  const cp = searchParams.get('cp');
  const idp = searchParams.get('idp');
  const deviceToken = searchParams.get('toolbox_user_token');
  const countryCode = searchParams.get('country');

  if (!cp || !idp) {
    throw Error(`Missing required parameters!`);
  }

  React.useEffect(() => {
      authGuard.init(cp, idp, deviceToken, countryCode);
  },
  // eslint-disable-next-line
  []);

  if (authGuard.configState?.isLoading) {
    return;
  }

  if (!authGuard.configState?.authenticated) {
    const successLoginReturnURL = authGuard.affServiceInstance?.getBindingInitializationURL();
    const redirectTo = authGuard.cpassServiceInstance?.getLoginURL({
      countryCode: authGuard.configState!.countryCode!,
      idpShortName: authGuard.configState!.idp!,
      returnURL: successLoginReturnURL
    });

    window.location.replace(redirectTo!);
    return null;
  }

  return children;
}
